import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Fade } from "react-awesome-reveal";
import { IoClose } from "react-icons/io5";
import { HiOutlineArrowLongLeft } from "react-icons/hi2";
import {
  fetchDiseases,
  fetchDrugs,
  saveAdditionalInformation,
} from "../../services/proAnalyse";
import {
  Disease,
  Drug,
  Question,
  FormData,
  AdditionalInformation,
} from "./interfaces";
import { fetchTest, startAnalysis } from "../../services/test";
import AdditionalInformationImg from "../../assets/images/additional-information.png";
import { BiChevronLeft } from "react-icons/bi";

const ProAdditionalInformation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [diseases, setDiseases] = useState<Disease[]>([]);
  const [drugs, setDrugs] = useState<Drug[]>([]);
  const [testInfo, setTestInfo] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [start, setStart] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [diseasesData, drugsData, testData] = await Promise.all([
          fetchDiseases(),
          fetchDrugs(),
          fetchTest(Number(id)),
        ]);

        setDiseases(diseasesData);
        setDrugs(drugsData);
        setTestInfo(testData.data);
      } catch (err) {
        setError("خطا در دریافت اطلاعات. لطفا دوباره تلاش کنید.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  //@ts-ignore
  const questions: Question[] = useMemo(() => {
    const baseQuestions = [
      {
        title: "لطفا وزنت رو وارد کن.",
        subtitle: "وزن برای محاسبه BMI استفاده می‌شه.",
        type: "number",
      },
      {
        title: "لطفا قدت رو وارد کن.",
        subtitle: "قد برای محاسبه BMI استفاده می‌شه.",
        type: "number",
      },
      {
        title: "اطلاعات مربوط به سوابق بیماری:",
        subtitle: "بیماری‌ها تاثیر مستقیمی روی نتیجه آزمایش دارن.",
        type: "radio",
        items: [
          { text: "بیماری خاصی داشته‌ام", value: true },
          { text: "بیماری خاصی نداشته‌ام", value: false },
        ],
        innerItems: {
          title: "انتخاب بیماری‌‌ها",
          type: "select",
          options: diseases ? diseases.map((item: any) => item.fa_name) : [],
        },
      },
      {
        title: "اطلاعات مربوط به سوابق بیماری خانوادگی:",
        subtitle: "بیماری‌های خانواده ممکنه روی نتیجه آزمایش اثر بذارن.",
        type: "radio",
        items: [
          { text: "سابقه بیماری خاصی داشته‌ام", value: true },
          { text: "سابقه بیماری خاصی نداشته‌ام", value: false },
        ],
        innerItems: {
          title: "انتخاب بیماری‌‌ها",
          type: "select",
          options: diseases ? diseases.map((item: any) => item.fa_name) : [],
        },
      },
      {
        title: "سوابق جراحی:",
        subtitle: "جراحی می‌تونه روی نتیجه آزمایش اثر بذاره.",
        type: "radio",
        items: [
          { text: "سابقه جراحی داشته‌ام", value: true },
          { text: "سابقه جراحی نداشته‌ام", value: false },
        ],
        innerItems: {
          title: "توضیحات بیشتر",
          type: "textarea",
        },
      },
    ];

    const menstrualStatusQuestion = {
      title: "وضعیت عادت ماهیانه:",
      subtitle:
        "جابجایی هورمون‌ها می‌تونه اثر زیادی روی وضعیت عادت ماهیانه داشته باشه.",
      type: "radio",
      items: [
        { text: "منظم", value: "Regular" },
        { text: "نامنظم", value: "Irregular" },
        { text: "دوران بارداری یا شیردهی", value: "Pregnant" },
      ],
    };

    const additionalQuestions = [
      {
        title: "داروهای در حال مصرف:",
        subtitle:
          "ممکنه دارویی رو مصرف کنی و همین باعث بشه نتیجه آزمایشت متفاوت بشه از حالت عادی.",
        type: "radio",
        items: [
          { text: "در حال مصرف دارو خاصی هستم", value: true },
          { text: "داروی خاصی مصرف نمی‌کنم", value: false },
        ],
        innerItems: {
          title: "انتخاب داروها",
          type: "select",
          options: drugs ? drugs.map((item: any) => item.fa_name) : [],
        },
      },
      {
        title: "داروهای مصرف‌شده در شش ماه گذشته:",
        subtitle:
          "ممکنه دارویی رو مصرف کرده باشی و همین باعث بشه نتیجه آزمایشت متفاوت بشه از حالت عادی.",
        type: "radio",
        items: [
          { text: "سابقه مصرف دارو داشته‌ام", value: true },
          { text: "سابقه مصرف دارو نداشته‌ام", value: false },
        ],
        innerItems: {
          title: "انتخاب داروها",
          type: "select",
          options: drugs ? drugs.map((item: any) => item.fa_name) : [],
        },
      },
      {
        title: "میزان مصرف سیگار:",
        subtitle:
          "سیگار و اثری که روی بدن داره، می‌تونه نتیجه آزمایش رو تغییر بده.",
        type: "radio",
        items: [
          { text: "سیگار مصرف نمی‌کنم", value: "No" },
          { text: "1 - 5 نخ سیگار در روز", value: "1-5" },
          { text: "5 - 10 نخ سیگار در روز", value: "5-10" },
          { text: "بیشتر از 10 نخ سیگار در روز", value: "10+" },
        ],
      },
      {
        title: "عادت‌های غذایی:",
        subtitle: "اگه حساسیت یا رژیم خاصی داشته باشی بهتره که ما بدونیم.",
        type: "radio",
        items: [
          { text: "رژیم غذایی خاصی دارم", value: true },
          { text: "رژیم غذایی خاصی را دنبال نمی‌کنم", value: false },
        ],
        innerItems: {
          title: "توضیحات بیشتر",
          type: "textarea",
        },
      },
      {
        title: "میزان خواب روزانه:",
        subtitle: "خواب و استراحت از مهم‌ترین فاکتورهای سلامتیه.",
        type: "radio",
        items: [
          { text: "کمتر از 6 ساعت", value: "Less than 6" },
          { text: "بین 6 تا 8 ساعت", value: "6-8" },
          { text: "بین 8 تا 10 ساعت", value: "8-10" },
          { text: "بیشتر از 10 ساعت", value: "10+" },
        ],
      },
      {
        title: "میزان فعالیت فیزیکی هفتگی:",
        subtitle: "اگه فعالیتت از حدی کمتر باشه ممکنه روی سلامتت اثرگذار باشه",
        type: "rate",
        items: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
      },
      {
        title: "میزان استرس روزانه:",
        subtitle: "فشار کاری و استرسی که تحمل می‌کنی روی سلامتت خیلی اثر داره",
        type: "rate",
        items: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
      },
    ];

    const gender = testInfo?.is_for_other
      ? testInfo?.temp_user?.gender
      : testInfo?.creator?.gender;

    return gender === "FEMALE"
      ? [...baseQuestions, ...additionalQuestions, menstrualStatusQuestion]
      : [...baseQuestions, ...additionalQuestions];
  }, [diseases, drugs]);

  const handleInputChange = (value: string, innerValue?: string[]) => {
    setFormData((prev) => ({
      ...prev,
      [currentStep]: {
        value,
        ...(innerValue !== undefined && { innerValue }),
      },
    }));
  };

  const currentQuestion = questions[currentStep - 1];
  const progress = (currentStep / questions.length) * 100;

  const handleNext = () => {
    if (currentStep < questions.length) {
      setCurrentStep((prev) => prev + 1);
      setSearchTerm("");
      setIsSelectOpen(false);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      setSearchTerm("");
      setIsSelectOpen(false);
    }
  };

  const startAnalysisHandler = () => {
    startAnalysis(Number(id)).then((response) => {
      if (response.status === 200) {
        navigate(`/test-analysis/${id}`);
        setIsSubmitLoading(false);
      }
    });
  };

  const handleSubmit = async () => {
    try {
      const submitData: AdditionalInformation = {};

      submitData.weight = formData[1]?.value
        ? Number(formData[1]?.value)
        : null;
      submitData.height = formData[2]?.value
        ? Number(formData[2]?.value)
        : null;

      //history_of_illness
      submitData.history_of_illness = formData[3]?.innerValue
        ?.map((diseaseName) => {
          //@ts-ignore
          const disease = diseases.find((d) => d.fa_name === diseaseName);
          return disease ? disease.id : -1;
        })
        .filter((id) => id !== -1);

      // family_history_of_illness
      submitData.family_history_of_illness = formData[4]?.innerValue
        ?.map((diseaseName) => {
          //@ts-ignore
          const disease = diseases.find((d) => d.fa_name === diseaseName);
          return disease ? disease.id : -1;
        })
        .filter((id) => id !== -1);

      //@ts-ignore
      submitData.has_surgeries = formData[5]?.value
        ? formData[5]?.value === "سابقه جراحی داشته‌ام"
          ? true
          : false
        : null;

      //@ts-ignore
      submitData.surgeries_description = formData[5]?.innerValue[0] ?? null;

      submitData.medications_currently = formData[6]?.innerValue
        ?.map((drugName) => {
          //@ts-ignore
          const drug = drugs.find((d) => d.fa_name === drugName);
          return drug ? drug.id : -1;
        })
        .filter((id) => id !== -1);

      submitData.history_of_medications = formData[7]?.innerValue
        ?.map((drugName) => {
          //@ts-ignore
          const drug = drugs.find((d) => d.fa_name === drugName);
          return drug ? drug.id : -1;
        })
        .filter((id) => id !== -1);

      //@ts-ignore
      submitData.smoking_status = formData[8]?.value
        ? formData[8]?.value
        : null;

      //@ts-ignore
      submitData.has_diet = formData[9]?.value
        ? formData[9]?.value
          ? true
          : false
        : null;

      //@ts-ignore
      submitData.diet_description = formData[9]?.innerValue[0]
        ? formData[9]?.innerValue[0]
        : null;

      //@ts-ignore
      submitData.sleep_amount = formData[10]?.value
        ? formData[10]?.value
        : null;

      //@ts-ignore
      submitData.exercise_level = formData[11]?.value
        ? formData[11]?.value
        : null;

      //@ts-ignore
      submitData.stress_level = formData[12]?.value
        ? formData[12]?.value
        : null;

      //@ts-ignore
      submitData.menstrual_status = formData[13]?.value
        ? formData[13]?.value === "منظم"
          ? "Regular"
          : formData[13]?.value === "نامنظم"
          ? "Irregular"
          : "Pregnant"
        : null;

      setIsSubmitLoading(true);

      await saveAdditionalInformation(submitData);

      if (submitData) {
        startAnalysisHandler();
      }
    } catch (err) {
      setIsSubmitLoading(false);
      setError("خطا در ارسال اطلاعات. لطفا دوباره تلاش کنید.");
    }
  };

  const handleSelectItem = (item: string) => {
    const currentInnerValue = formData[currentStep]?.innerValue || [];
    const newInnerValue = currentInnerValue.includes(item)
      ? currentInnerValue.filter((i) => i !== item)
      : [...currentInnerValue, item];

    handleInputChange(formData[currentStep]?.value || "", newInnerValue);
    // Clear the search term first, then close the select dropdown
    setSearchTerm("");
    setTimeout(() => {
      setIsSelectOpen(false);
    }, 0);
  };

  const handleSelectClick = () => {
    setIsSelectOpen(!isSelectOpen);
  };

  const handleSearchClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsSelectOpen(true);
    // Don't clear the search term when clicking on the search field
  };

  const renderQuestionInput = () => {
    const currentValue = formData[currentStep]?.value || "";
    const currentInnerValue = formData[currentStep]?.innerValue || [];

    switch (currentQuestion.type) {
      case "number":
        return (
          <div className="form-control">
            <input
              type="number"
              className="input input-bordered w-full"
              value={currentValue}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder="لطفا عدد وارد کنید"
              dir="rtl"
            />
          </div>
        );

      case "rate":
        return (
          <div className="rating rating-xl">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((star, idx) => (
              <input
                type="radio"
                name={`rating-${currentStep}`}
                className={`mask mask-star-2 ml-2 
              ${
                (currentStep === questions.length &&
                  !formData[questions.length]?.value) ||
                (currentStep == questions.length - 1 &&
                  !formData[Number(questions.length) - 1]?.value)
                  ? "bg-red-100"
                  : "bg-red-400"
              }
              `}
                aria-label={`${idx + 1} star`}
                key={star}
                checked={formData[currentStep]?.value === star.toString()}
                onChange={() => handleInputChange(star.toString())}
              />
            ))}
          </div>
        );

      case "radio":
        return (
          <div className="space-y-6">
            <div className="form-control">
              {currentQuestion.items?.map((item, index) => (
                <label
                  key={index}
                  className="label cursor-pointer justify-start gap-4"
                >
                  <input
                    type="radio"
                    name={`question-${currentStep}`}
                    className="radio"
                    //@ts-ignore
                    value={item.value}
                    //@ts-ignore
                    checked={String(currentValue) === String(item.value)}
                    //@ts-ignore
                    onChange={() => handleInputChange(String(item.value), [])}
                  />
                  <span className="label-text text-black">
                    {
                      //@ts-ignore
                      item.text
                    }
                  </span>
                </label>
              ))}
            </div>

            {currentQuestion.innerItems &&
              //@ts-ignore
              currentValue === currentQuestion.items?.[0].value.toString() && (
                <div className="form-control mt-4">
                  <label className="label">
                    <span className="label-text">
                      {currentQuestion.innerItems.title}
                    </span>
                  </label>
                  {currentQuestion.innerItems.type === "textarea" ? (
                    <textarea
                      className="textarea textarea-bordered h-24 bg-white"
                      value={currentInnerValue[0] || ""}
                      onChange={(e) =>
                        handleInputChange(currentValue, [e.target.value])
                      }
                      placeholder="لطفا توضیحات خود را وارد کنید"
                      dir="rtl"
                    />
                  ) : (
                    <div className="space-y-4">
                      <div className="relative">
                        <div
                          className="input input-bordered flex items-center cursor-pointer"
                          onClick={handleSelectClick}
                        >
                          <input
                            type="text"
                            className="w-full bg-transparent border-none focus:outline-none"
                            placeholder="جستجو..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onClick={handleSearchClick}
                          />
                        </div>

                        {isSelectOpen && currentQuestion.innerItems.options && (
                          <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
                            {currentQuestion.innerItems.options
                              .filter((option) => option.includes(searchTerm))
                              .map((option, index) => (
                                <div
                                  key={index}
                                  className={`p-2 cursor-pointer hover:bg-base-200 ${
                                    currentInnerValue.includes(option)
                                      ? "bg-base-200"
                                      : ""
                                  }`}
                                  onClick={() => handleSelectItem(option)}
                                >
                                  {option}
                                </div>
                              ))}
                          </div>
                        )}
                      </div>

                      {currentInnerValue.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {currentInnerValue.map((item, index) => (
                            <div
                              key={index}
                              className="bg-gray-200 px-3 py-1 rounded-xl gap-2 flex items-center"
                            >
                              <span className="text-xs">{item}</span>
                              <button
                                onClick={() => handleSelectItem(item)}
                                className="btn btn-ghost btn-xs p-0"
                              >
                                <IoClose size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
          </div>
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="loading loading-spinner loading-lg"></div>
          <p className="mt-4">در حال بارگذاری...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="alert alert-error">
            <span>{error}</span>
          </div>
          <button
            className="btn btn-primary mt-4"
            onClick={() => window.location.reload()}
          >
            تلاش مجدد
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1">
      {!start ? (
        <Fade triggerOnce>
          <div className="flex-1 h-full flex items-center justify-between min-h-[80vh] gap-20">
            <div className="flex-1">
              <h1 className="font-rokh font-[900] text-2xl text-center lg:text-right">
                شروع تحلیل پیشرفته بادی‌بان
              </h1>
              <p className="font-rokh font-[700] mb-5 text-center lg:text-right">
                برای تفسیر پیشرفته آزمایشت، لازمه بهمون کمک کنی تا درک بهتری از
                شرایطت داشته باشیم!
              </p>
              <p className="text-sm lg:text-base mb-16">
                توی حالت پیشرفته تحلیل آزمایش، ما یک سری سوال از شما می‌پرسیم
                درباره شرایط فعلی شما که این موارد می‌تونن روی نتیجه آزمایش شما
                اثرگذار باشن. هوش‌مصنوعی بادی‌بان در ادامه با توجه به اطلاعاتی
                که از شما گرفته و آزمایشی که بارگذاری کردید یه تحلیل خیلی خیلی
                دقیق‌تر درباره وضعیت سلامت شما بهتون می‌ده. <br />
                این رو مطمئن باشید که همه اطلاعات شما پیش ما کاملا محرمانه
                می‌مونه و ازشون صرفا برای تحلیل اطلاعات شخص شما استفاده می‌شه.
              </p>

              {testInfo?.additional_details?.weight && (
                <p className="font-rokh font-[700] text-center lg:text-right max-w-md">
                  اطلاعات سلامت شما قبلا دریافت شده، اگه لازمه که تغییرش بدی
                  دکمه ویرایش رو بزن و اگه نه دکمه شروع تحلیل رو بزن.
                </p>
              )}

              <div className="flex items-center gap-3 mt-3">
                <button
                  className="flex-1 btn bg-pr_primary text-white px-3 py-2 border-none flex items-center hover:bg-pr_blue_dark hover:border-none lg:flex-[0.3]"
                  onClick={() => setStart(true)}
                >
                  {testInfo?.additional_details?.weight ? "ویرایش" : "شروع"}
                  <BiChevronLeft size={25} />
                </button>
                {testInfo?.additional_details?.weight && (
                  <button
                    className="flex-1 btn bg-pr_primary text-white px-3 py-2 border-none flex items-center hover:bg-pr_blue_dark hover:border-none lg:flex-[0.3]"
                    onClick={() => startAnalysisHandler()}
                  >
                    شروع تحلیل
                    <BiChevronLeft size={25} />
                  </button>
                )}
              </div>
            </div>
            <div className="flex-[1] hidden lg:flex">
              <img
                src={AdditionalInformationImg}
                alt="additional-information"
                className="w-full"
              />
            </div>
          </div>
        </Fade>
      ) : (
        <div className="max-w-2xl mx-auto px-4 mt-20" dir="rtl">
          <div>
            <div className="w-full mb-20">
              <p className="font-rokh font-[700] mb-3 text-center lg:text-right">
                برای تفسیر پیشرفته آزمایشت، لازمه بهمون کمک کنی تا درک بهتری از
                شرایطت داشته باشیم!
              </p>
              <progress
                className="progress w-full"
                value={progress}
                max="100"
              ></progress>
              <div className="text-left font-rokh font-[700]">
                <strong className="font-rokh font-[900]">{currentStep}</strong>/
                {questions.length}
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-0">
                  {currentQuestion.title}
                </h2>
                <p className="text-sm text-gray-500 mb-6">
                  {currentQuestion.subtitle}
                </p>
              </div>

              {renderQuestionInput()}

              {/* Navigation buttons */}
              <div className="flex justify-between mt-8">
                <button
                  className="px-6 pb-2 pt-3 font-rokh font-[700] rounded-xl text-white text-sm bg-pr_primary hover:bg-pr_blue_dark"
                  onClick={handlePrevious}
                  disabled={currentStep === 1}
                >
                  قبلی
                </button>

                {currentStep === questions.length ? (
                  <button
                    disabled={isSubmitLoading}
                    className="px-6 pb-2 pt-3 font-rokh font-[700] rounded-xl text-white text-sm bg-pr_primary hover:bg-pr_blue_dark"
                    onClick={handleSubmit}
                  >
                    {isSubmitLoading ? "در حال ثبت" : "ثبت"}
                  </button>
                ) : (
                  <button
                    className="px-6 pb-2 pt-3 font-rokh font-[700] rounded-xl text-white text-sm bg-pr_primary hover:bg-pr_blue_dark"
                    onClick={handleNext}
                  >
                    بعدی
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProAdditionalInformation;
